import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../context/LanguageContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaFilter } from 'react-icons/fa';
import { FaInstagram, FaYoutube, FaTelegram, FaFacebookF, FaTwitter, FaTwitch, FaGlobe } from 'react-icons/fa';
import { SiTiktok, SiSpotify, SiReddit, SiGoogle } from 'react-icons/si';
import { USDT_ADDRESS, USDT_ABI, USDT_DECIMALS } from '../config/contracts';
import {
  USDT_ESCROW_ABI,
  USDT_ESCROW_ADDRESS,
  USDT_ESCROW_V2_ADDRESS,
  USDT_DEPOSIT_ADDRESS,
} from '../config/usdtEscrow';
import WalletConnector from './WalletConnector';
import './SMMPanel.css';

const SMMPanel = () => {
  const { user, isAuthenticated, login, logout, isLoading: authLoading } = useAuth();
  const { isConnected, account, web3 } = useWeb3();
  const [showWalletConnector, setShowWalletConnector] = useState(false);
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [refreshingAllOrders, setRefreshingAllOrders] = useState(false);
  const [credits, setCredits] = useState({ available: '0', reserved: '0' });
  const [creditsError, setCreditsError] = useState(false);
  const [userDiscount, setUserDiscount] = useState(null); // { discountPercent, expiresAt }
  const [depositAmount, setDepositAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  
  // Order form state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [orderLink, setOrderLink] = useState('');
  const [orderQuantity, setOrderQuantity] = useState('');
  const [activeTab, setActiveTab] = useState('new_order'); // 'new_order' | 'orders'
  const [orderMode, setOrderMode] = useState('new'); // 'new' | 'search' | 'mass' (UI parity; only 'new' active)
  const [platformFilter, setPlatformFilter] = useState('All');
  const [serviceSearch, setServiceSearch] = useState('');

  const hasShownServicesErrRef = useRef(false);
  const hasShownOrdersErrRef = useRef(false);
  const hasShownWalletMismatchRef = useRef(false);

  const authedWalletLower = (user?.walletAddress || '').toLowerCase();
  const connectedWalletLower = (account || '').toLowerCase();
  const walletMismatch =
    Boolean(isAuthenticated && isConnected && authedWalletLower && connectedWalletLower && authedWalletLower !== connectedWalletLower);

  useEffect(() => {
    if (!walletMismatch) return;
    if (hasShownWalletMismatchRef.current) return;
    hasShownWalletMismatchRef.current = true;
    toast.error('Wallet mismatch. Please sign in again with your connected wallet.');
    logout();
    setServices([]);
    setOrders([]);
    setCredits({ available: '0', reserved: '0' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletMismatch]);

  const handleAuthFailure = useCallback((error) => {
    const status = error?.response?.status;
    if (status === 401) {
      // Token expired/invalid: force sign-in prompt instead of confusing “input” errors.
      logout();
      toast.error(t('smmSignInExpired') || 'Session expired. Please sign in again.');
      return true;
    }
    return false;
  }, [logout, t]);

  // Fetch services
  const fetchServices = useCallback(async () => {
    try {
      setServicesLoading(true);
      const response = await api.get('/smm/services');
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      if (handleAuthFailure(error)) return;
      if (!hasShownServicesErrRef.current) {
        toast.error(t('smmFailedToLoadServices'));
        hasShownServicesErrRef.current = true;
      }
    } finally {
      setServicesLoading(false);
    }
  }, [handleAuthFailure, t]);

  const fetchCredits = useCallback(async () => {
    try {
      const res = await api.get('/smm/credits');
      setCredits(res.data.credits || { available: '0', reserved: '0' });
      setCreditsError(false);
      return true;
    } catch (error) {
      console.error('Error fetching credits:', error);
      if (handleAuthFailure(error)) return false;
      // Fallback: read credits directly from chain. Use the authenticated wallet
      // (account is often null when the wallet lives in an extension), so a failed
      // backend read still resolves the real on-chain balance.
      const readWallet = (account || user?.walletAddress || '').toLowerCase();
      try {
        if (web3 && readWallet && USDT_ESCROW_ADDRESS) {
          // Phase 2: a user's effective balance can live across BOTH escrow
          // contracts (legacy v1 credit being drained, plus new v2 deposits).
          // Read both in parallel and sum so the displayed number matches what
          // the backend orchestrator actually sees during reserves.
          const v1c = new web3.eth.Contract(USDT_ESCROW_ABI, USDT_ESCROW_ADDRESS);
          const calls = [
            v1c.methods.availableBalance(readWallet).call(),
            v1c.methods.reservedBalance(readWallet).call(),
          ];
          if (USDT_ESCROW_V2_ADDRESS) {
            const v2c = new web3.eth.Contract(USDT_ESCROW_ABI, USDT_ESCROW_V2_ADDRESS);
            calls.push(
              v2c.methods.availableBalance(readWallet).call(),
              v2c.methods.reservedBalance(readWallet).call()
            );
          }
          const results = await Promise.all(calls);
          const v1Avail = BigInt(String(results[0] || '0'));
          const v1Res   = BigInt(String(results[1] || '0'));
          const v2Avail = BigInt(String(results[2] || '0'));
          const v2Res   = BigInt(String(results[3] || '0'));
          setCredits({
            available: String(v1Avail + v2Avail),
            reserved:  String(v1Res + v2Res),
          });
          setCreditsError(false);
          return true;
        }
      } catch (chainErr) {
        console.error('Chain credits fallback failed:', chainErr);
      }
      // Couldn't read at all — flag it so the UI shows "unavailable" instead of a
      // misleading "0.00" (the on-chain funds are safe regardless).
      setCreditsError(true);
      return false;
    }
  }, [account, handleAuthFailure, web3, user]);

  const fetchDiscount = useCallback(async () => {
    try {
      const res = await api.get('/smm/my-discount');
      setUserDiscount(res.data.discount || null);
    } catch (error) {
      console.error('Error fetching discount:', error);
    }
  }, []);

  // Fetch user orders
  const fetchOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);
      const response = await api.get('/smm/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (handleAuthFailure(error)) return;
      if (!hasShownOrdersErrRef.current) {
        toast.error(t('smmFailedToLoadOrders'));
        hasShownOrdersErrRef.current = true;
      }
    } finally {
      setOrdersLoading(false);
    }
  }, [handleAuthFailure, t]);

  useEffect(() => {
    // Important: SMM endpoints are protected by JWT auth (server-side).
    // Only fetch after the user has signed in; otherwise show a sign-in prompt.
    if (isAuthenticated && !walletMismatch) {
      fetchServices();
      fetchOrders();
      fetchCredits();
      fetchDiscount();
    }
  }, [isAuthenticated, walletMismatch, fetchServices, fetchOrders, fetchCredits, fetchDiscount]);

  const shortAddr = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  const compactServiceName = (name, maxLen = 72) => {
    const clean = String(name || '').replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    if (clean.length <= maxLen) return clean;
    return `${clean.slice(0, Math.max(0, maxLen - 1))}…`;
  };

  // SMM providers typically return rate as "price per 1000 units".
  const parseRate = (rate) => {
    const n = parseFloat(String(rate ?? '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const estimateTotalUsd = (service, quantity) => {
    const ratePer1k = parseRate(service?.rate);
    const qty = parseInt(quantity || 0, 10);
    if (!service || !Number.isFinite(qty) || qty <= 0) return 0;
    return (ratePer1k * qty) / 1000;
  };

  const estimateDiscountedUsd = (service, quantity) => {
    const base = estimateTotalUsd(service, quantity);
    if (!userDiscount || !userDiscount.discountPercent) return base;
    return base * (1 - userDiscount.discountPercent / 100);
  };

  const formatUnits = (value, decimals = USDT_DECIMALS, places = 2) => {
    try {
      const s = String(value || '0');
      if (decimals !== 18 || !web3) {
        // Fallback: basic decimal formatting from string
        const big = BigInt(s);
        const base = 10n ** BigInt(decimals);
        const whole = big / base;
        const frac = (big % base).toString().padStart(decimals, '0').slice(0, Math.min(decimals, places));
        return `${whole.toString()}.${frac.padEnd(places, '0')}`;
      }
      const asEth = web3.utils.fromWei(s, 'ether');
      const n = Number(asEth);
      if (!Number.isFinite(n)) return asEth;
      return n.toFixed(places);
    } catch (_) {
      return '0.00';
    }
  };

  const handleDeposit = async () => {
    if (!web3 || !account) {
      toast.error('Connect your wallet first.');
      return;
    }
    // Deposit target: v2 when configured (so new credits land alongside the
    // refund pool), otherwise v1 — same `deposit(amount)` ABI on both.
    if (!USDT_DEPOSIT_ADDRESS) {
      toast.error('Escrow contract not configured.');
      return;
    }
    const amt = Number(depositAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a deposit amount.');
      return;
    }

    const recordDeposit = async (txHash, amountWei) => {
      if (!txHash || !amountWei) return;
      try {
        const chainId = await web3.eth.getChainId();
        await api.post('/smm/deposits/record', { txHash, amountWei, chainId });
      } catch (err) {
        console.error('Record deposit failed:', err);
      }
    };

    try {
      setDepositing(true);
      const usdtContract = new web3.eth.Contract(USDT_ABI, USDT_ADDRESS);
      const escrowContract = new web3.eth.Contract(USDT_ESCROW_ABI, USDT_DEPOSIT_ADDRESS);

      // Convert to token units (USDT on BSC = 18 decimals)
      const amountWei = web3.utils.toWei(String(amt), 'ether');

      // Pre-check balance so we don't trigger gas-estimation failure / huge gas fallback
      const usdtBal = await usdtContract.methods.balanceOf(account).call();
      if (BigInt(usdtBal) < BigInt(amountWei)) {
        toast.error('Insufficient USDT balance for this deposit.');
        return;
      }

      // Allowance is per-spender — when deposits redirect to v2 the user
      // needs a fresh approve for v2 (their old v1 approve does NOT carry).
      const allowance = await usdtContract.methods.allowance(account, USDT_DEPOSIT_ADDRESS).call();
      if (BigInt(allowance) < BigInt(amountWei)) {
        const maxApproval = web3.utils.toWei('1000000000', 'ether'); // large approval for convenience
        // Provide a tight gas limit to avoid wallets quoting absurd fees if estimate fails
        const approveGas = await usdtContract.methods
          .approve(USDT_DEPOSIT_ADDRESS, maxApproval)
          .estimateGas({ from: account });
        await usdtContract.methods
          .approve(USDT_DEPOSIT_ADDRESS, maxApproval)
          .send({ from: account, gas: Math.floor(Number(approveGas) * 1.2) });
      }

      // Estimate gas first; if this throws, the tx is very likely to revert.
      let depositGas;
      try {
        depositGas = await escrowContract.methods.deposit(amountWei).estimateGas({ from: account });
      } catch (estErr) {
        // Try to surface a cleaner revert reason (simulation)
        try {
          await escrowContract.methods.deposit(amountWei).call({ from: account });
        } catch (callErr) {
          const msg = callErr?.message || estErr?.message || 'Deposit would fail';
          toast.error(msg);
          return;
        }
        toast.error(estErr?.message || 'Deposit would fail');
        return;
      }

      // Avoid TransactionBlockTimeoutError by tracking txHash and polling receipt ourselves.
      const sendDeposit = () =>
        new Promise((resolve, reject) => {
          let txHash = null;
          escrowContract.methods
            .deposit(amountWei)
            .send({ from: account, gas: Math.floor(Number(depositGas) * 1.2) })
            .once('transactionHash', (h) => {
              txHash = h;
              toast.info(`Deposit submitted: ${h.slice(0, 10)}…`);
            })
            .once('receipt', (rcpt) => resolve(rcpt))
            .once('error', (err) => {
              // If we have a hash but web3 timed out waiting, keep polling instead of failing.
              if (txHash && String(err?.message || '').toLowerCase().includes('timeout')) {
                resolve({ transactionHash: txHash, status: null });
                return;
              }
              reject(err);
            });
        });

      const initial = await sendDeposit();
      const txHash = initial?.transactionHash;
      if (initial?.status === true) {
        toast.success('Deposit successful');
        await recordDeposit(txHash, amountWei);
      } else if (txHash) {
        // Poll receipt for up to ~10 minutes
        const start = Date.now();
        while (Date.now() - start < 10 * 60 * 1000) {
          const r = await web3.eth.getTransactionReceipt(txHash);
          if (r && r.status != null) {
            if (r.status) {
              toast.success('Deposit successful');
              await recordDeposit(txHash, amountWei);
            }
            else toast.error('Deposit failed');
            break;
          }
          await new Promise((res) => setTimeout(res, 4000));
        }
      }

      setDepositAmount('');
      // The credited balance only updates once the deposit tx is mined+indexed.
      // Poll a few times so it appears without the user manually refreshing.
      fetchCredits();
      for (let i = 0; i < 6; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        const ok = await fetchCredits();
        if (ok) break;
      }
    } catch (e) {
      console.error('Deposit error:', e);
      toast.error(e?.message || 'Deposit failed');
    } finally {
      setDepositing(false);
    }
  };

  // Normalize platform from service category/name (white-labeled)
  const getPlatformFromService = (s) => {
    const hay = `${s?.category || ''} ${s?.name || ''}`.toLowerCase();
    if (hay.includes('instagram')) return 'Instagram';
    if (hay.includes('youtube')) return 'YouTube';
    if (hay.includes('tiktok') || hay.includes('tik tok')) return 'TikTok';
    if (hay.includes('spotify')) return 'Spotify';
    if (hay.includes('telegram')) return 'Telegram';
    if (hay.includes('twitter') || hay.includes('x ')) return 'Twitter';
    if (hay.includes('facebook')) return 'Facebook';
    if (hay.includes('reddit')) return 'Reddit';
    if (hay.includes('twitch')) return 'Twitch';
    if (hay.includes('seo') || hay.includes('backlink')) return 'SEO Backlinks';
    if (hay.includes('traffic') || hay.includes('website')) return 'Website Traffic';
    return 'Other';
  };

  const formatServiceOptionLabel = (service) => {
    if (!service) return '';
    const platform = getPlatformFromService(service);
    const rate = service?.rate != null ? `$${service.rate}` : '';
    const name = compactServiceName(service.name, 64);
    // Keep options short so native <select> doesn't truncate to an unreadable blob.
    return `#${service.service} — ${platform} • ${name}${rate ? ` (${rate})` : ''}`;
  };

  const platforms = useMemo(() => {
    const base = ['All', 'Instagram', 'YouTube', 'TikTok', 'Spotify', 'Telegram', 'Twitter', 'Facebook', 'Reddit', 'Website Traffic', 'SEO Backlinks', 'Other'];
    // Always show the full tile grid (even if services fail to load)
    return base;
  }, []);

  const firstCategoryForPlatform = (platform) => {
    if (!platform || platform === 'All') return '';
    const cats = [
      ...new Set(
        services
          .filter((s) => getPlatformFromService(s) === platform)
          .map((s) => s.category)
          .filter(Boolean)
      )
    ];
    return cats[0] || '';
  };

  // Get unique categories (from API)
  const categories = useMemo(() => {
    return [...new Set(services.map(s => s.category))].filter(Boolean);
  }, [services]);

  // Filter services by category
  const filteredServices = useMemo(() => {
    let out = services;
    if (platformFilter && platformFilter !== 'All') {
      out = out.filter(s => getPlatformFromService(s) === platformFilter);
    }
    if (selectedCategory) {
      out = out.filter(s => s.category === selectedCategory);
    }
    return out;
  }, [services, selectedCategory, platformFilter]);

  const searchResults = useMemo(() => {
    const q = String(serviceSearch || '').trim().toLowerCase();
    if (!q) return [];
    let out = services;
    if (platformFilter && platformFilter !== 'All') {
      out = out.filter((s) => getPlatformFromService(s) === platformFilter);
    }
    out = out.filter((s) => {
      const hay = `${s?.service ?? ''} ${s?.category ?? ''} ${s?.name ?? ''} ${s?.type ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
    return out.slice(0, 60);
  }, [serviceSearch, services, platformFilter]);

  // Handle service selection
  const handleServiceSelect = (service) => {
    // Multi-provider: services are looked up by their opaque internal code
    // (PS-XXXX) which can be a string. If the lookup didn't resolve, bail
    // cleanly instead of crashing on `service.min`.
    if (!service) {
      setSelectedService(null);
      setOrderQuantity('');
      return;
    }
    setSelectedService(service);
    setOrderQuantity(service.min || '');
  };

  const selectServiceFromSearch = (service) => {
    if (!service) return;
    setSelectedCategory(service.category || '');
    handleServiceSelect(service);
    setOrderMode('new');
  };

  // Place order
  const handlePlaceOrder = async () => {
    if (!selectedService) {
      toast.error(t('smmSelectService'));
      return;
    }
    if (!orderLink.trim()) {
      toast.error(t('smmPleaseEnterLink'));
      return;
    }
    if (!orderQuantity || orderQuantity < selectedService.min || orderQuantity > selectedService.max) {
      toast.error(
        t('smmQuantityBetween')
          .replace('{min}', String(selectedService.min))
          .replace('{max}', String(selectedService.max))
      );
      return;
    }

    // First request after the host has been idle can take 20-30s while
    // Phusion Passenger cold-starts the Node app on Namecheap. After 10s
    // we update the button label so the user knows the click was received
    // and we're waiting on the upstream, not stuck.
    let slowMsgTimer = null;
    try {
      setLoading(true);
      slowMsgTimer = setTimeout(() => {
        toast.info('Still working — provider can be slow on the first request after idle.', { autoClose: 6000 });
      }, 10000);

      const response = await api.post('/smm/orders', {
        service: selectedService.service,
        serviceName: selectedService.name,
        link: orderLink,
        quantity: parseInt(orderQuantity)
      });

      // Order is on-chain / on-provider as soon as the POST resolves. Show
      // success + release the button NOW; refetches happen in the background
      // so the UI stops feeling "stuck" right after the actual work is done.
      toast.success(`${t('smmOrderPlaced')} ${response.data.order}`);
      if (response?.data?.creditsAfter) {
        setCredits(response.data.creditsAfter);
      }
      setOrderLink('');
      setOrderQuantity(selectedService.min || '');
      setSelectedService(null);
      setActiveTab('orders');
      // Fire-and-forget the refetches — already toasted success, nothing
      // gates further interaction on these finishing.
      Promise.all([fetchOrders(), fetchCredits()]).catch(() => {});
    } catch (error) {
      console.error('Error placing order:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data
      });
      if (handleAuthFailure(error)) return;
      const backendMsg = error?.response?.data?.message;
      const backendErr = error?.response?.data?.error;
      const msg =
        backendErr ||
        backendMsg ||
        error?.message ||
        t('smmFailedToPlaceOrder');
      toast.error(msg);
    } finally {
      if (slowMsgTimer) clearTimeout(slowMsgTimer);
      setLoading(false);
    }
  };

  // Refresh order status
  const refreshOrderStatus = async (orderId) => {
    try {
      await api.get(`/smm/orders/${orderId}/status`);
      fetchOrders();
    } catch (error) {
      console.error('Error refreshing order status:', error);
    }
  };

  const refreshAllOrders = async () => {
    if (refreshingAllOrders) return;
    try {
      setRefreshingAllOrders(true);
      await api.post('/smm/orders/refresh-status', { limit: Math.max(orders.length, 50) });
      fetchOrders();
    } catch (error) {
      console.error('Error refreshing all orders:', error);
    } finally {
      setRefreshingAllOrders(false);
    }
  };

  // Cancel order
  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(t('smmCancelConfirm'))) {
      return;
    }

    try {
      await api.post(`/smm/orders/${orderId}/cancel`);
      toast.success('Order canceled successfully');
      fetchOrders();
    } catch (error) {
      console.error('Error canceling order:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    }
  };

  const canCancelOrder = (status) => {
    const s = String(status || '').toLowerCase().trim();
    // Only allow cancel while still pending (provider hasn't started processing).
    return s === 'pending';
  };

  if (!isAuthenticated && !isConnected) {
    return (
      <div className="smm-panel">
        <div className="smm-embed smm-embed--locked">
          <div className="smm-locked-card">
            <div className="smm-locked-title">{t('smmPanelLockedTitle')}</div>
            <div className="smm-locked-subtitle">{t('smmConnectToUse')}</div>
          </div>
        </div>
      </div>
    );
  }

  // Wallet connected but not signed-in (JWT): show prompt instead of spamming API calls/toasts.
  if (!isAuthenticated) {
    return (
      <div className="smm-panel">
        <div className="smm-embed">
          <aside className="smm-sidebar">
            <div className="smm-sidebar-brand">
              <div className="smm-sidebar-brandtext">
                <div className="smm-sidebar-title">PHONESTREAM</div>
                <div className="smm-sidebar-subtitle">SMM</div>
              </div>
            </div>
            <div className="smm-sidebar-user">
              <div className="smm-avatar">{(account || 'P').substring(2, 3).toUpperCase()}</div>
              <div className="smm-user-meta">
                <div className="smm-user-name">My Account</div>
                <div className="smm-user-sub">{isConnected ? shortAddr(account) : '—'}</div>
              </div>
            </div>
          </aside>

          <section className="smm-main">
            <div className="smm-hero">
              <div className="smm-hero-title">{t('smmSignInRequiredTitle')}</div>
              <div className="smm-hero-subtitle">{t('smmSignInRequiredBody')}</div>
              <div className="smm-actions" style={{ marginTop: 12 }}>
                <button
                  className="smm-primary"
                  onClick={async () => {
                    try {
                      await login();
                    } catch (e) {
                      toast.error(e?.message || 'Sign in failed');
                    }
                  }}
                  disabled={authLoading || !isConnected}
                >
                  {authLoading ? t('connecting') : t('smmSignInNow')}
                </button>
              </div>
            </div>

            <div className="smm-platforms">
              {platforms.map((p) => (
                <button
                  key={p}
                  className={`smm-platform-chip ${platformFilter === p ? 'active' : ''}`}
                  onClick={() => {
                    setPlatformFilter(p);
                    const firstCat = firstCategoryForPlatform(p);
                    setSelectedCategory(firstCat);
                    setSelectedService(null);
                  }}
                  type="button"
                >
                  <span className="smm-platform-icon" aria-hidden="true">{platformIcon(p)}</span>
                  <span>{p}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  function platformIcon(platform) {
    switch (platform) {
      case 'All':
        return <FaFilter />;
      case 'Instagram':
        return <FaInstagram />;
      case 'YouTube':
        return <FaYoutube />;
      case 'TikTok':
        return <SiTiktok />;
      case 'Spotify':
        return <SiSpotify />;
      case 'Telegram':
        return <FaTelegram />;
      case 'Facebook':
        return <FaFacebookF />;
      case 'Twitter':
        return <FaTwitter />;
      case 'Twitch':
        return <FaTwitch />;
      case 'Reddit':
        return <SiReddit />;
      case 'SEO Backlinks':
        return <SiGoogle />;
      case 'Website Traffic':
        return <FaGlobe />;
      default:
        return <FaGlobe />;
    }
  }

  return (
    <div className="smm-panel">
      <div className="smm-embed">
        <aside className="smm-sidebar">
          <div className="smm-sidebar-brand">
            <div className="smm-sidebar-brandtext">
              <div className="smm-sidebar-title">PHONESTREAM</div>
              <div className="smm-sidebar-subtitle">SMM</div>
            </div>
          </div>

          <div className="smm-sidebar-user">
            <div className="smm-avatar">{(account || 'P').substring(2, 3).toUpperCase()}</div>
            <div className="smm-user-meta">
              <div className="smm-user-name">My Account</div>
              <div className="smm-user-sub">{isConnected ? shortAddr(account) : 'Authenticated'}</div>
            </div>
          </div>

          <nav className="smm-nav">
            <button
              className={`smm-nav-item ${activeTab === 'new_order' ? 'active' : ''}`}
              onClick={() => setActiveTab('new_order')}
            >
              {t('smmNewOrder')}
            </button>
            <button
              className={`smm-nav-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              {t('smmOrders')}
            </button>
            <button
              className={`smm-nav-item ${activeTab === 'credits' ? 'active' : ''}`}
              onClick={() => setActiveTab('credits')}
            >
              {t('smmCredits')}
            </button>
            <button
              className="smm-nav-item"
              onClick={() => {
                fetchServices();
                toast.info(t('smmRefreshingServices'));
              }}
            >
              {t('smmServices')}
            </button>
          </nav>

          <div className="smm-sidebar-footer">
            <div className="smm-mini-stat" style={{ marginTop: 10 }}>
              <div className="smm-mini-label">{t('smmCredits')}</div>
              <div className="smm-mini-value">
                {formatUnits(credits.available, USDT_DECIMALS, 2)} USDT
              </div>
              <div className="smm-mini-label" style={{ marginTop: 6 }}>{t('smmReserved')}</div>
              <div className="smm-mini-value">
                {formatUnits(credits.reserved, USDT_DECIMALS, 2)} USDT
              </div>
            </div>
          </div>
        </aside>

        <section className="smm-main">
          <div className="smm-topbar">
            <div className="smm-breadcrumb">
              {t('smmHome')} <span className="sep">›</span>{' '}
              {activeTab === 'orders' ? t('smmOrders') : activeTab === 'credits' ? t('smmCredits') : t('smmNewOrderTab')}
            </div>
            <div className="smm-topbar-right">
              <div className="smm-pill">
                <span className="smm-pill-label">USDT</span>
                <span className="smm-pill-value">{formatUnits(credits.available, USDT_DECIMALS, 2)}</span>
              </div>
              {isConnected && (
                <div className="smm-pill smm-pill--muted">
                  {shortAddr(account)}
                </div>
              )}
            </div>
          </div>

          <div className="smm-hero">
            <div className="smm-hero-title">{t('smmPanelTitle')}</div>
            <div className="smm-hero-subtitle">{t('smmPanelSubtitle')}</div>
          </div>

          <div className="smm-stats">
            <div className="smm-stat-card">
              <div className="smm-stat-label">{t('smmYourCredits')}</div>
              <div className="smm-stat-value">
                {formatUnits(credits.available, USDT_DECIMALS, 2)} <span className="smm-stat-unit">USDT</span>
              </div>
              <div className="smm-mini-label" style={{ marginTop: 8 }}>{t('smmReserved')}: {formatUnits(credits.reserved, USDT_DECIMALS, 2)} USDT</div>
            </div>
            <div className="smm-stat-card">
              <div className="smm-stat-label">{t('smmTotalOrders')}</div>
              <div className="smm-stat-value">{orders.length.toLocaleString()}</div>
            </div>
            <div className="smm-stat-card">
              <div className="smm-stat-label">{t('smmSelectedPlatform')}</div>
              <div className="smm-stat-value">{platformFilter}</div>
            </div>
            <div className="smm-stat-card">
              <div className="smm-stat-label">{t('smmStatus')}</div>
              <div className="smm-stat-value">{selectedService ? t('smmReady') : t('smmSelectServiceStatus')}</div>
            </div>
          </div>

          <div className="smm-platforms">
            {platforms.map((p) => (
              <button
                key={p}
                className={`smm-platform-chip ${platformFilter === p ? 'active' : ''}`}
                onClick={() => {
                  setPlatformFilter(p);
                  const firstCat = firstCategoryForPlatform(p);
                  setSelectedCategory(firstCat);
                  setSelectedService(null);
                }}
              >
                <span className="smm-platform-icon" aria-hidden="true">{platformIcon(p)}</span>
                <span>{p}</span>
              </button>
            ))}
          </div>

          {activeTab === 'credits' && (
            <div className="smm-grid">
              <div className="smm-card">
                <div className="smm-card-header">
                  <div className="smm-card-title">{t('smmAddFunds')}</div>
                </div>
                <div className="smm-form">
                  <div className="smm-field">
                    <label>{t('smmDepositAmount')} (USDT)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="10"
                      autoComplete="off"
                      disabled={depositing}
                    />
                    <div className="smm-service-fullname" style={{ marginTop: 10 }}>
                      {creditsError ? (
                        <div style={{ color: '#fbbf24', lineHeight: 1.5 }}>
                          ⚠ Couldn’t load your balance right now — <strong>your funds are safe on-chain</strong>.
                          This is a server read issue (the backend may need a restart), not a lost deposit.{' '}
                          <button
                            type="button"
                            onClick={fetchCredits}
                            style={{ background: 'none', border: 0, padding: 0, color: '#00e0ff', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                          >
                            Retry
                          </button>
                        </div>
                      ) : (
                        <>
                          {t('smmAvailable')}: <strong>{formatUnits(credits.available, USDT_DECIMALS, 2)} USDT</strong><br />
                          {t('smmReserved')}: <strong>{formatUnits(credits.reserved, USDT_DECIMALS, 2)} USDT</strong><br />
                          <span style={{ opacity: 0.9 }}>{t('smmDepositHelp')}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="smm-actions">
                    {account ? (
                      <button
                        className="smm-primary"
                        onClick={handleDeposit}
                        disabled={depositing}
                      >
                        {depositing ? t('smmDepositing') : t('smmDeposit')}
                      </button>
                    ) : (
                      <button
                        className="smm-primary"
                        onClick={() => setShowWalletConnector(true)}
                        type="button"
                      >
                        {t('connectWallet') || 'Connect Wallet'}
                      </button>
                    )}
                    <button
                      className="smm-secondary"
                      onClick={() => {
                        fetchCredits();
                        toast.info(t('smmRefreshingCredits'));
                      }}
                      disabled={depositing}
                    >
                      {t('smmRefresh')}
                    </button>
                  </div>
                  {!account && (
                    <div className="smm-service-fullname" style={{ marginTop: 8, opacity: 0.85 }}>
                      🔌 Connect your wallet to deposit USDT into your SMM balance.
                    </div>
                  )}
                </div>
              </div>
              <div className="smm-card">
                <div className="smm-card-header">
                  <div className="smm-card-title">{t('smmServicePreview')}</div>
                </div>
                <div className="smm-muted-box">
                  USDT escrow is enabled for testing. Funds are held on-chain and automatically applied to orders.
                </div>
              </div>
            </div>
          )}

          {activeTab === 'new_order' && (
            <div className="smm-grid">
              <div className="smm-card">
                <div className="smm-card-header">
                  <div className="smm-card-title">{t('smmPlaceYourOrder')}</div>
                  <div className="smm-subtabs">
                    <button className={`smm-subtab ${orderMode === 'new' ? 'active' : ''}`} onClick={() => setOrderMode('new')}>
                      {t('smmNewOrderTab')}
                    </button>
                    <button className={`smm-subtab ${orderMode === 'search' ? 'active' : ''}`} onClick={() => setOrderMode('search')}>
                      {t('smmSearchTab')}
                    </button>
                    <button className={`smm-subtab ${orderMode === 'mass' ? 'active' : ''}`} onClick={() => setOrderMode('mass')}>
                      {t('smmMassOrderTab')}
                    </button>
                  </div>
                </div>

                {orderMode !== 'new' ? (
                  orderMode === 'search' ? (
                    <div className="smm-form">
                      <div className="smm-field">
                        <label>{t('smmSearchTab')}</label>
                        <input
                          type="text"
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                          placeholder="Search by ID, name, or category"
                        />
                      </div>

                      {serviceSearch.trim() === '' ? (
                        <div className="smm-muted-box">Type to search services.</div>
                      ) : searchResults.length === 0 ? (
                        <div className="smm-muted-box">No services found.</div>
                      ) : (
                        <div className="smm-search-results">
                          {searchResults.map((s) => (
                            <button
                              key={s.service}
                              type="button"
                              className="smm-search-item"
                              onClick={() => selectServiceFromSearch(s)}
                            >
                              <div className="smm-search-name">{s.name}</div>
                              <div className="smm-search-meta">
                                #{s.service} · {s.category || 'Other'} · ${s.rate}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="smm-muted-box">
                      {t('smmComingSoon')}
                    </div>
                  )
                ) : (
                  <div className="smm-form">
                    {userDiscount?.discountPercent && (
                      <div className="smm-discount-banner">
                        🎉 You have a <strong>{userDiscount.discountPercent}% discount</strong> applied to all orders
                        {userDiscount.expiresAt ? ` (expires ${new Date(userDiscount.expiresAt).toLocaleDateString()})` : ''}.
                      </div>
                    )}
                    <div className="smm-field">
                      <label>{t('smmCategory')}</label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => {
                          setSelectedCategory(e.target.value);
                          setSelectedService(null);
                        }}
                      >
                        <option value="">{t('smmAllCategories')}</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="smm-field">
                      <label>{t('smmService')}</label>
                      {servicesLoading ? (
                        <div className="smm-skeleton">{t('smmLoadingServices')}</div>
                      ) : (
                        <select
                          value={selectedService?.service || ''}
                          title={selectedService?.name || ''}
                          onChange={(e) => {
                            // Multi-provider: `service.service` is an opaque
                            // string code (SV-XXXX or PS-XXXX) since the
                            // backend started routing across providers.
                            // String-compare on both sides so the lookup
                            // works for either format.
                            const v = String(e.target.value);
                            if (!v) {
                              handleServiceSelect(null);
                              return;
                            }
                            // Search BOTH services and filteredServices so a
                            // brief stale-state window (e.g. mid-refetch)
                            // doesn't make the selection silently fail.
                            const svc = services.find(s => String(s.service) === v)
                                     || filteredServices.find(s => String(s.service) === v);
                            if (!svc) {
                              // Observable failure — previously we silently
                              // bailed, which made the dropdown look like it
                              // ignored the click. Surface the cause.
                              console.warn('[smm] service lookup failed', {
                                clicked_value: v,
                                services_count: services.length,
                                filtered_count: filteredServices.length,
                                sample_codes: services.slice(0, 6).map(s => s.service),
                              });
                              toast.error('Could not select that service. Please refresh the page.');
                              return;
                            }
                            handleServiceSelect(svc);
                          }}
                        >
                          <option value="">{t('smmSelectService')}</option>
                          {filteredServices.map(service => (
                            <option key={service.service} value={service.service}>
                              {formatServiceOptionLabel(service)}
                            </option>
                          ))}
                        </select>
                      )}
                      {selectedService?.name ? (
                        <div className="smm-service-fullname" title={selectedService.name}>
                          {selectedService.name}
                        </div>
                      ) : null}
                    </div>

                    <div className="smm-field">
                      <label>{t('smmLink')}</label>
                      <input
                        type="text"
                        value={orderLink}
                        onChange={(e) => setOrderLink(e.target.value)}
                        placeholder="https://..."
                        disabled={!selectedService}
                      />
                    </div>

                    <div className="smm-field">
                      <label>{t('smmQuantity')} {selectedService ? `(min ${selectedService.min}, max ${selectedService.max})` : ''}</label>
                      <input
                        type="number"
                        value={orderQuantity}
                        onChange={(e) => setOrderQuantity(e.target.value)}
                        min={selectedService?.min}
                        max={selectedService?.max}
                        disabled={!selectedService}
                      />
                    </div>

                    {selectedService && orderLink && orderQuantity && (
                      <div className="smm-order-summary">
                        <div className="smm-order-summary-title">Order Summary</div>
                        <div className="smm-order-summary-row">
                          <span>Service</span>
                          <span>{selectedService.name}</span>
                        </div>
                        <div className="smm-order-summary-row">
                          <span>Quantity</span>
                          <span>{Number(orderQuantity).toLocaleString()}</span>
                        </div>
                        <div className="smm-order-summary-row">
                          <span>Rate</span>
                          <span>${selectedService.rate} / 1000</span>
                        </div>
                        {userDiscount?.discountPercent ? (
                          <>
                            <div className="smm-order-summary-row smm-order-summary-row--strikethrough">
                              <span>Subtotal</span>
                              <span>${estimateTotalUsd(selectedService, orderQuantity).toFixed(2)}</span>
                            </div>
                            <div className="smm-order-summary-row smm-order-summary-row--discount">
                              <span>Discount ({userDiscount.discountPercent}% off)</span>
                              <span>−${(estimateTotalUsd(selectedService, orderQuantity) - estimateDiscountedUsd(selectedService, orderQuantity)).toFixed(2)}</span>
                            </div>
                            <div className="smm-order-summary-row smm-order-summary-row--total">
                              <span>You Pay</span>
                              <span>${estimateDiscountedUsd(selectedService, orderQuantity).toFixed(2)} USDT</span>
                            </div>
                          </>
                        ) : (
                          <div className="smm-order-summary-row smm-order-summary-row--total">
                            <span>Total</span>
                            <span>${estimateTotalUsd(selectedService, orderQuantity).toFixed(2)} USDT</span>
                          </div>
                        )}
                        <div className="smm-order-summary-row smm-order-summary-row--balance">
                          <span>Your Balance</span>
                          <span>{formatUnits(credits.available, USDT_DECIMALS, 2)} USDT</span>
                        </div>
                      </div>
                    )}

                    <div className="smm-actions">
                      <button
                        className="smm-primary"
                        onClick={handlePlaceOrder}
                        disabled={loading || !selectedService || !orderLink || !orderQuantity}
                      >
                        {loading ? t('smmPlacingOrder') : t('smmPlaceOrder')}
                      </button>
                      <button
                        className="smm-secondary"
                        onClick={() => {
                          setSelectedService(null);
                          setOrderLink('');
                          setOrderQuantity('');
                        }}
                        disabled={loading}
                      >
                        {t('smmReset')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="smm-card smm-card--preview">
                <div className="smm-card-header">
                  <div className="smm-card-title">{t('smmServicePreview')}</div>
                </div>

                {!selectedService ? (
                  <div className="smm-preview-empty">
                    {t('smmSelectServicePreview')}
                  </div>
                ) : (
                  <div className="smm-preview">
                    <div className="smm-preview-name">{selectedService.name}</div>
                    <div className="smm-preview-sub">
                      <span className="smm-preview-pill">ID: {selectedService.service}</span>
                      <span className="smm-preview-pill">{getPlatformFromService(selectedService)}</span>
                      <span className="smm-preview-pill">${selectedService.rate}</span>
                    </div>

                    <div className="smm-preview-metrics">
                      <div className="smm-metric">
                        <div className="smm-metric-label">{t('smmMin')}</div>
                        <div className="smm-metric-value">{selectedService.min}</div>
                      </div>
                      <div className="smm-metric">
                        <div className="smm-metric-label">{t('smmMax')}</div>
                        <div className="smm-metric-value">{selectedService.max}</div>
                      </div>
                      <div className="smm-metric">
                        <div className="smm-metric-label">{t('smmRefill')}</div>
                        <div className="smm-metric-value">{selectedService.refill ? t('smmYes') : t('smmNo')}</div>
                      </div>
                      <div className="smm-metric">
                        <div className="smm-metric-label">{t('smmCancel')}</div>
                        <div className="smm-metric-value">{selectedService.cancel ? t('smmYes') : t('smmNo')}</div>
                      </div>
                    </div>

                    <div className="smm-preview-desc">
                      <div className="smm-preview-desc-title">{t('smmDescription')}</div>
                      <div className="smm-preview-desc-body">
                        {selectedService.description ||
                          selectedService.effectiveDescription ||
                          selectedService.adminDescription ||
                          selectedService.providerDescription ||
                          'No description available.'}
                      </div>
                      <div className="smm-preview-desc-body" style={{ marginTop: 8, opacity: 0.85 }}>
                        Category: {selectedService.category || '—'}<br />
                        Type: {selectedService.type || '—'}<br />
                        Rate: ${selectedService.rate}<br />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="smm-card">
              <div className="smm-card-header">
                <div className="smm-card-title">{t('smmMyOrders')}</div>
                <button
                  className="smm-secondary"
                  onClick={refreshAllOrders}
                  disabled={ordersLoading || refreshingAllOrders}
                >
                  {ordersLoading || refreshingAllOrders ? t('smmLoadingOrders') : t('smmRefreshAll')}
                </button>
              </div>

              {ordersLoading ? (
                <div className="smm-skeleton">{t('smmLoadingOrders')}</div>
              ) : orders.length === 0 ? (
                <div className="smm-muted-box">{t('smmNoOrders')}</div>
              ) : (
                <div className="smm-orders">
                  {orders.map(order => (
                    <div key={order._id} className="smm-order-row">
                      <div className="smm-order-left">
                        <div className="smm-order-title">{order.serviceName}</div>
                        <div className="smm-order-meta">
                          <span className="smm-order-pill">{t('smmOrder')} #{order.orderId}</span>
                          <span className="smm-order-pill">{t('smmQty')} {order.quantity}</span>
                          {order?.remains != null && (
                            <span className="smm-order-pill">
                              {t('smmRemains')} {order.remains}
                            </span>
                          )}
                          {order?.escrowAmount && (
                            <span className="smm-order-pill">
                              Paid {formatUnits(order.escrowAmount, USDT_DECIMALS, 2)} USDT
                            </span>
                          )}
                          {!order?.escrowAmount && order?.chargedUsd != null && (
                            <span className="smm-order-pill">
                              Paid {Number(order.chargedUsd).toFixed(2)} USDT
                            </span>
                          )}
                        </div>
                        <div className="smm-order-link">
                          <a href={order.link} target="_blank" rel="noopener noreferrer">{order.link}</a>
                        </div>
                      </div>

                      <div className="smm-order-right">
                        <span className={`smm-status smm-status--${order.status.toLowerCase().replaceAll(' ', '-')}`}>
                          {order.status}
                        </span>
                        <div className="smm-order-actions">
                          <button className="smm-secondary" onClick={() => refreshOrderStatus(order._id)}>
                            {t('smmRefresh')}
                          </button>
                          {canCancelOrder(order.status) && (
                            <button className="smm-danger" onClick={() => handleCancelOrder(order._id)}>
                              {t('smmCancel')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {showWalletConnector && (
        <WalletConnector
          onClose={() => setShowWalletConnector(false)}
          onConnected={() => {
            setShowWalletConnector(false);
            fetchCredits();
          }}
        />
      )}
    </div>
  );
};

export default SMMPanel;
